import { useEffect, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { auth } from '../firebase';
import ConfirmModal from '../components/ConfirmModal';
import useAdminAction from '../hooks/useAdminAction';
import { ADMIN_ACTIONS } from '../constants/adminActions';
import {
  adminTaskRef,
  estimateBroadcastRecipients,
  featureFlagsRef,
  fetchFeatureFlags,
} from '../services/firestore';

const FEATURE_FLAG_KEYS = [
  'enablePhoneAuth',
  'enableStorefront',
  'enableBarcode',
  'enableLoyaltyProgram',
];

export default function Operations() {
  const { performAction, loading: actionLoading, error: actionError } = useAdminAction();
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmMode, setConfirmMode] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [message, setMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [estimatedCount, setEstimatedCount] = useState(0);
  const [previewing, setPreviewing] = useState(false);

  const loadFlags = async () => {
    setLoading(true);
    const latestFlags = await fetchFeatureFlags();
    setFlags(latestFlags);
    setLoading(false);
  };

  useEffect(() => {
    loadFlags();
  }, []);

  const toggleMaintenanceMode = async () => {
    if (!flags) return;
    const nextValue = !Boolean(flags.maintenanceMode);
    await performAction({
      action: ADMIN_ACTIONS.MAINTENANCE_MODE_TOGGLED,
      targetBusinessId: null,
      before: { maintenanceMode: Boolean(flags.maintenanceMode) },
      after: { maintenanceMode: nextValue },
      writeFn: (batch, batchDb) => {
        batch.set(featureFlagsRef(batchDb), { maintenanceMode: nextValue }, { merge: true });
      },
    });
    setConfirmMode(false);
    await loadFlags();
  };

  const toggleFeatureFlag = async (flag) => {
    if (!flags) return;
    const nextValue = !Boolean(flags[flag]);
    await performAction({
      action: ADMIN_ACTIONS.FEATURE_FLAG_TOGGLED,
      targetBusinessId: null,
      before: { flag, value: Boolean(flags[flag]) },
      after: { flag, value: nextValue },
      writeFn: (batch, batchDb) => {
        batch.set(featureFlagsRef(batchDb), { [flag]: nextValue }, { merge: true });
      },
    });
    await loadFlags();
  };

  const previewRecipients = async () => {
    setPreviewing(true);
    try {
      const count = await estimateBroadcastRecipients(filter);
      setEstimatedCount(count);
    } finally {
      setPreviewing(false);
    }
  };

  const queueBroadcast = async () => {
    const taskId = `broadcastSms_${Date.now()}`;
    const payload = {
      message: message.trim(),
      filter,
      estimatedCount,
      createdAt: Timestamp.now(),
      createdBy: auth.currentUser?.email || 'unknown',
      status: 'pending',
    };

    await performAction({
      action: ADMIN_ACTIONS.BROADCAST_SMS_QUEUED,
      targetBusinessId: null,
      before: null,
      after: { filter, estimatedCount },
      writeFn: (batch, batchDb) => {
        batch.set(adminTaskRef(batchDb, taskId), payload);
      },
    });

    setConfirmSend(false);
    setMessage('');
    setEstimatedCount(0);
  };

  if (loading) return <div className="wb-card">Loading operations...</div>;

  return (
    <>
      <div className={`wb-card ${flags?.maintenanceMode ? 'wb-maintenance-on' : ''}`}>
        <h3 className="wb-section-title">Maintenance Mode</h3>
        <p className="wb-muted">
          Current state:
          {' '}
          <strong>{flags?.maintenanceMode ? 'ON' : 'OFF'}</strong>
        </p>
        <button type="button" className={flags?.maintenanceMode ? 'danger' : ''} onClick={() => setConfirmMode(true)}>
          {flags?.maintenanceMode ? 'Turn OFF' : 'Turn ON'}
        </button>
      </div>

      <div className="wb-card">
        <h3 className="wb-section-title">Feature Flags</h3>
        {FEATURE_FLAG_KEYS.map((flag) => (
          <div className="wb-row wb-flag-row" key={flag}>
            <strong>{flag}</strong>
            <button type="button" disabled={actionLoading} onClick={() => toggleFeatureFlag(flag)}>
              {flags?.[flag] ? 'Disable' : 'Enable'}
            </button>
          </div>
        ))}
      </div>

      <div className="wb-card">
        <h3 className="wb-section-title">Broadcast SMS</h3>
        <textarea
          rows={4}
          maxLength={160}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Message"
        />
        <p className="wb-muted">{message.length}/160</p>
        <select value={filter} onChange={(event) => setFilter(event.target.value)}>
          <option value="all">All merchants</option>
          <option value="trial">Trial only</option>
          <option value="active">Active paid only</option>
        </select>
        <div className="wb-row">
          <button type="button" onClick={previewRecipients} disabled={previewing}>
            {previewing ? 'Previewing...' : 'Preview'}
          </button>
          <span className="wb-muted">Estimated recipients: {estimatedCount}</span>
        </div>
        <button
          type="button"
          onClick={() => setConfirmSend(true)}
          disabled={!message.trim() || estimatedCount === 0}
        >
          Send
        </button>
      </div>

      {actionError ? <p className="wb-error-text">{actionError}</p> : null}

      <ConfirmModal
        open={confirmMode}
        title="Confirm Maintenance Toggle"
        description={flags?.maintenanceMode ? 'Turn maintenance mode OFF?' : 'Turn maintenance mode ON?'}
        risk="high"
        onCancel={() => setConfirmMode(false)}
        onConfirm={toggleMaintenanceMode}
      />

      <ConfirmModal
        open={confirmSend}
        title="Confirm SMS Broadcast"
        description={`Send to ${estimatedCount} merchants? This cannot be undone.`}
        risk="high"
        onCancel={() => setConfirmSend(false)}
        onConfirm={queueBroadcast}
      />
    </>
  );
}
